import { ChevronRight, FileText, LockKeyhole, Printer, RefreshCw } from "lucide-react";
import { useState } from "react";
import { clients } from "../data";
import { useOpsSnapshot } from "../hooks/useOps";

const money = (value: number) => new Intl.NumberFormat("ko-KR").format(value);

export function SettlementView() {
  const { data, sources } = useOpsSnapshot();
  const [clientId, setClientId] = useState(clients[0].id);
  const [previewOpen, setPreviewOpen] = useState(true);
  const selected = clients.find((client) => client.id === clientId) ?? clients[0];
  const selectedRollup = data?.customersMonth.find((row) => row.customer === selected.name);
  const mealCount = Number(selectedRollup?.meal_count ?? 0);
  const lunchboxCount = Number(selectedRollup?.lunchbox_count ?? 0);
  const ticketAmount = mealCount * (data?.unitPrice ?? 8_000);
  const lunchboxAmount = Number(selectedRollup?.lunchbox_amount ?? 0);
  const selectedTotal = Number(selectedRollup?.settlement ?? 0);
  const vat = Math.round(selectedTotal / 11);
  const monthTotal = data?.customersMonth.reduce((sum, row) => sum + Number(row.settlement), 0) ?? 0;
  const sqlReady = sources.find((source) => source.id === "sql")?.state === "connected";
  const lunchboxReady = sources.find((source) => source.id === "lunchbox")?.state === "connected";
  const ready = Boolean(data) && sqlReady && lunchboxReady;
  const monthLabel = data
    ? `${data.businessDate.slice(0, 4)}년 ${Number(data.businessDate.slice(5, 7))}월`
    : "원장 연결 대기";

  return (
    <>
      <section className="settlement-hero">
        <div>
          <span>{monthLabel} · 누적 정산</span>
          <h2>{data ? money(monthTotal) : "—"}원</h2>
          <p>식권카드·도시락만 고객사 식대정산에 반영합니다. POS·신용카드는 제외됩니다.</p>
        </div>
        <div className="settlement-countdown">
          <span>월말 석식까지</span>
          <strong>4일</strong>
        </div>
        <button onClick={() => document.getElementById("settlement-form")?.scrollIntoView({ behavior: "smooth" })}>
          정산서 작성 <ChevronRight size={17} />
        </button>
      </section>

      <div className={`settlement-workspace ${previewOpen ? "" : "preview-closed"}`}>
        <article className="panel settlement-form" id="settlement-form">
          <div className="settlement-panel-head">
            <div>
              <h2>정산서 작성</h2>
              <span>주간 누적 초안 D04</span>
            </div>
            <span className="draft-badge">{ready ? "주간 누적 원장" : "원장 연결 대기"}</span>
          </div>

          <div className="settlement-fields">
            <label>
              <span>정산월</span>
              <select aria-label="정산월"><option>2026년 07월</option></select>
            </label>
            <label>
              <span>고객사</span>
              <select aria-label="정산 고객사" value={clientId} onChange={(event) => setClientId(event.target.value)}>
                {clients.map((client) => <option value={client.id} key={client.id}>{client.name}</option>)}
              </select>
            </label>
          </div>

          <div className="settlement-fields">
            <label>
              <span>식권·도시락 단가</span>
              <div className="unit-field"><input aria-label="식권과 도시락 단가" type="number" defaultValue={8000} /><em>원</em></div>
            </label>
            <div className="field-readonly">
              <span>정산기간</span>
              <strong>2026.07.01 – 기준일 석식</strong>
            </div>
          </div>

          <div className="calculation-rule">
            <RefreshCw size={17} />
            <div><span>집계 원칙</span><strong>이전 주 금액에 더하지 않고, 매주 월 1일부터 기준일 석식까지 원장을 전량 재계산합니다.</strong></div>
          </div>

          <div className="settlement-steps" aria-label="주간 누적 갱신 단계">
            {[
              ["1주차", "1일–7일 석식"],
              ["2주차", "1일–14일 석식"],
              ["3주차", "1일–21일 석식"],
              ["4주차", "1일–기준일 석식"],
              ["월말 확정", "1일–말일 석식"],
            ].map(([label, range], index) => (
              <button key={label} className={index === 3 ? "current" : ""} disabled>
                <span>{label}</span>
                <strong>{range}</strong>
                <em>{index < 4 ? "검증 대기" : "예정"}</em>
              </button>
            ))}
          </div>

          <div className="validation-grid">
            <div><span className="check-wait"><LockKeyhole size={15} /></span><strong>식권 원장</strong><em>{sqlReady ? "연결됨" : "연결 대기"}</em></div>
            <div><span className="check-wait"><LockKeyhole size={15} /></span><strong>도시락 원장</strong><em>{lunchboxReady ? "연결됨" : "연결 대기"}</em></div>
            <div><span className="check-wait"><LockKeyhole size={15} /></span><strong>합계 검증</strong><em>{ready ? "자동 재계산" : "대사 전"}</em></div>
          </div>

          <div className="settlement-totals">
            <div><span>식권카드</span><strong>{ready ? money(mealCount) : "—"} 식</strong><em>{ready ? money(ticketAmount) : "—"}원</em></div>
            <div><span>도시락</span><strong>{ready ? money(lunchboxCount) : "—"} 개</strong><em>{ready ? money(lunchboxAmount) : "—"}원</em></div>
            <div className="grand"><span>부가세 포함</span><strong>{ready ? money(selectedTotal) : "—"}원</strong><em>VAT {ready ? money(vat) : "—"}원</em></div>
          </div>

          <div className="settlement-actions">
            <button className="outline-button" onClick={() => setPreviewOpen((value) => !value)}>
              <FileText size={17} /> {previewOpen ? "미리보기 닫기" : "미리보기 열기"}
            </button>
            <button className="outline-button" onClick={() => window.print()}><Printer size={17} /> A4 인쇄</button>
            <button className="primary-button" disabled><RefreshCw size={17} /> 주간 누적 업데이트</button>
          </div>
          <p className="settlement-help">월말 최종본은 말일 석식 수집·대사 완료 후에만 확정됩니다.</p>
        </article>

        {previewOpen && <SettlementPreview clientName={selected.name} teams={selected.teams} total={ready ? selectedTotal : null} />}
      </div>

      <article className="panel settlement-clients">
        <div className="section-header">
          <div><h2>고객사별 정산 누적</h2><span>기준일 석식 · 원장 연결 대기</span></div>
        </div>
        <div className="settlement-table">
          <div className="settlement-row head">
            <span>고객사</span><span>식권카드</span><span>도시락</span><span>부가세</span><span>누적 정산액</span><span>작성</span>
          </div>
          {clients.map((client) => {
            const row = data?.customersMonth.find((item) => item.customer === client.name);
            const rowTicket = Number(row?.meal_count ?? 0) * (data?.unitPrice ?? 8_000);
            const rowLunchbox = Number(row?.lunchbox_amount ?? 0);
            const rowTotal = Number(row?.settlement ?? 0);
            return (
            <div className="settlement-row" key={client.id}>
              <span><strong>{client.name}</strong><em>{client.teams.length ? `${client.teams.length}개 팀·부서` : "직속 고객사"}</em></span>
              <span>{row ? money(rowTicket) : "—"}원</span><span>{row ? money(rowLunchbox) : "—"}원</span><span>{row ? money(Math.round(rowTotal / 11)) : "—"}원</span><span className="row-total">{row ? money(rowTotal) : "—"}원</span>
              <button onClick={() => { setClientId(client.id); document.getElementById("settlement-form")?.scrollIntoView({ behavior: "smooth" }); }}>작성</button>
            </div>
          )})}
        </div>
      </article>
    </>
  );
}

function SettlementPreview({ clientName, teams, total }: { clientName: string; teams: string[]; total: number | null }) {
  const [page, setPage] = useState(0);
  const pages = ["표지", "식권 전체", ...teams.slice(0, 5), "도시락"];

  return (
    <article className="panel settlement-preview-panel">
      <div className="preview-head">
        <div><h2>{clientName} 식대정산서</h2><span>{pages.length}면 · 미확정</span></div>
        <button className="outline-button" onClick={() => window.print()}><Printer size={16} /> 인쇄</button>
      </div>
      <div className="page-tabs" aria-label="정산서 페이지">
        {pages.map((label, index) => (
          <button className={page === index ? "active" : ""} key={`${label}-${index}`} onClick={() => setPage(index)}>
            <span>{index + 1}</span>{label}
          </button>
        ))}
      </div>
      <div className="paper-scroll">
        <div className="settlement-paper" aria-label="식대정산서 미리보기">
          <div className="paper-draft">주간 누적 · 미확정</div>
          <div className="paper-logo"><span>YF</span><strong>YURIMFOOD</strong></div>
          <h3>식 대 정 산 서</h3>
          <div className="document-meta">
            <span>문서번호 : YF-202607-{clientName}-D04</span>
            <span>발행일 : 원장 연결 후 생성</span>
            <span>정산기간 : 2026.07.01 - 기준일 석식</span>
          </div>
          <div className="party-grid">
            <div>
              <h4>공급받는자</h4>
              <p><span>상호</span><strong>{clientName}</strong></p>
              <p><span>사업자번호</span><strong>승인 후 표시</strong></p>
              <p><span>대표자</span><strong>승인 후 표시</strong></p>
              <p><span>주소</span><strong>승인 후 표시</strong></p>
              <p><span>담당자</span><strong>정산 담당자</strong></p>
            </div>
            <div>
              <h4>공급자</h4>
              <p><span>상호</span><strong>유림푸드</strong></p>
              <p><span>사업자번호</span><strong>409-86-*****</strong></p>
              <p><span>대표자</span><strong>김용운</strong></p>
              <p><span>주소</span><strong>경기도 용인시 처인구 원삼면 ***</strong></p>
              <p><span>연락처</span><strong>010-****-6564</strong></p>
            </div>
          </div>
          <div className="paper-total">
            <span>합 계 금 액 (공급가액 + 부가세)</span>
            <strong>{total === null ? "—" : money(total)} 원</strong>
            <em>공급가액 {total === null ? "—" : money(total - Math.round(total / 11))} + 부가세 {total === null ? "—" : money(Math.round(total / 11))}</em>
          </div>
          <table>
            <thead><tr><th>항목</th><th>수량</th><th>단가(원)</th><th>금액(원)</th></tr></thead>
            <tbody>
              <tr><td>식권카드</td><td>— 식</td><td>8,000</td><td>—</td></tr>
              <tr><td>도시락</td><td>— 개</td><td>8,000</td><td>—</td></tr>
            </tbody>
            <tfoot>
              <tr><td colSpan={3}>공급가액</td><td>—</td></tr>
              <tr><td colSpan={3}>부가세 (10%)</td><td>—</td></tr>
              <tr><td colSpan={3}>합계금액</td><td>—</td></tr>
            </tfoot>
          </table>
          <ul>
            <li>위 금액은 식권카드와 도시락 원장을 대사한 후 생성됩니다.</li>
            <li>POS·신용·체크카드 매출은 고객사 식대정산에서 제외됩니다.</li>
          </ul>
          <div className="paper-sign">
            <span>원장 연결 후 발행일 확정</span>
            <div><strong>주식회사 유림푸드</strong><span>대표이사 김 용운</span></div>
            <em>직인 없음</em>
          </div>
        </div>
      </div>
      <p className="privacy-note"><LockKeyhole size={14} /> 공개 화면에서는 사업자번호·주소·연락처·직인을 마스킹합니다.</p>
    </article>
  );
}
